import _ from 'lodash';
import axios from 'axios';
import { useState, useEffect } from 'react';

import PrivateWealthTeamsService from '../../services/PrivateWealthTeamsService';
import PwtWarningsService from '../../services/PwtWarningsService';
import GlobalSearchService from '../../services/GlobalSearchService';

import useAdminNotes from '../../../../hooks/useAdminNotes';
import useEffectExceptOnMount from '../../../../helpers/useEffectExceptOnMount';
import { useFieldWithValidation } from '../../../../hooks';
import useAccolades from './useAccolades';
import useAumAggregations from './useAumAggregations';
import useConfirmDialog from './useConfirmDialog';
import useFirmInfo from './useFirmInfo';

import { validEmail, validUrl } from '../../../../helpers/utils';

const VALIDATE_LOCATION_ERROR = 'Locations must have fields filled out';
const DUPLICATE_LOCATION_ERROR = 'Team has duplicated locations';
const MISSING_PRINCIPAL_ERROR = 'At least one location must be marked as principal';
const VALIDATE_PRE_DRAFT_LOCATION_ERROR = 'The location can only be associated with a PWG if all associated contacts work there';
const VALIDATE_HISTORICAL_BLUEPRINT_ERROR = 'Historical Blueprints must have fields filled out';
const DUPLICATE_HISTORICAL_BLUEPRINT_ERROR = 'Team has duplicated historical blueprints';

const teamFirmInfoInitialState = {
  team_name: '',
  url: '',
  aum: undefined,
  specialties: [],
  client_types: [],
  investments_utilized: [],
  minimum_account_size: undefined,
  crd_id: undefined,
  firm_name: '',
  email: '',
  photo: null,
  photoPreview: '',
  locations: [],
  historical_blueprints: [],
  pwt_type: null,
};

const optionDataInitialState = {
  open: false,
  cities: [],
  countries: [],
  states: [],
  services: [],
  investmentUtilized: [],
  clientTypes: [],
  pwtTypes: [],
  unitedStatesId: undefined
};

const usePrivateWealthTeamForm = ({
  session,
  team,
  activeData,
  onClose,
  open,
  afterCreate,
  onDataUpdate,
  itemIndex,
  goBack,
  fetchPrivateWealthTeams,
  itemsInQueue,
  hasQueue,
  snackbar,
  draftData,
  fromWarnings,
  onChangeNotes
}) => {
  const { confirmDialog, closeConfirmDialog, openConfirmDialog } = useConfirmDialog();
  const {
    aumAggregations,
    fetchingAumAggregations,
    fetchAumAggregations,
    handleAddAumAggregation,
    handleRemoveAumAggregation,
    handleChangeAumAggregation,
    handleBlurAumAggregation,
  } = useAumAggregations({
    team,
    session,
    openConfirmDialog,
    onAggregationsChange: (updated) => {
      setTeamFirmInfo(prev => ({
        ...prev,
        pwt_aum_aggregations_attributes: updated,
      }));
    },
    onUserChange: () => {
      setHasChanges(true);
      setIsFromUserChange(true);
    },
  });
  const {
    accolades,
    fetchingAccolades,
    fetchStatusAccolades,
    excludedAccolades,
    fetchingExcludedAccolades,
    fetchStatusExcludedAccolades,
    updatingAccolade,

    fetchAccolades,
    fetchExcludedAccolades,
    handleAddAccoladeToTeam,
    handleRemoveAccoladeFromTeam,
  } = useAccolades({ team, session, openConfirmDialog });
  const { getFirmAumAggregationInfo } = useFirmInfo();

  const [teamFirmInfo, setTeamFirmInfo] = useState(teamFirmInfoInitialState);

  const [teamMembers, setTeamMembers] = useState([]);
  const [formerTeamMembers, setFormerTeamMembers] = useState([]);
  const [potentialTeamMembers, setPotentialTeamMembers] = useState([]);

  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [selectedExcludedSpecialties, setExcludedSelectedSpecialties] = useState([]);
  const [selectedExcludedInvestmentUtilized, setExcludedSelectedInvestmentUtilized] = useState([]);
  const [selectedExcludedClientTypes, setExcludedSelectedClientTypes] = useState([]);
  const [selectedClientTypes, setSelectedClientTypes] = useState([]);
  const [selectedInvestmentUtilized, setSelectedInvestmentUtilized] = useState([]);

  const [expandFirmInfo, setExpandFirmInfo] = useState(false);
  const [expandTeamMembers, setExpandTeamMembers] = useState(false);
  const [expandPotentialTeamMembers, setExpandPotentialTeamMembers] = useState(false);

  const [sortBy, setSortBy] = useState('first_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [formerSortBy, setFormerSortBy] = useState('first_name');
  const [potentialSortBy, setPotentialSortBy] = useState('contact_first_name');
  const [formerSortDirection, setFormerSortDirection] = useState('asc');
  const [potentialSortDirection, setPotentialSortDirection] = useState('asc');

  const [totalItems, setTotalItems] = useState(0);
  const [totalItemsFormer, setTotalItemsFormer] = useState(0);
  const [totalItemsPotential, setTotalItemsPotential] = useState(0);
  const [initialTotalItems, setInitialTotalItems] = useState(-1);
  const [initialTotalItemsFormer, setInitialTotalItemsFormer] = useState(-1);

  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageFormer, setCurrentPageFormer] = useState(0);
  const [currentPagePotential, setCurrentPagePotential] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [rowsPerPageFormer, setRowsPerPageFormer] = useState(50);
  const [rowsPerPagePotential, setRowsPerPagePotential] = useState(50);

  const [selectedContact, setSelectedSelectedContact] = useState({});
  const [selectedContactCrdId, setSelectedContactCrdId] = useState();
  const [openContactModal, setOpenContactModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState({});
  const [linkAction, setLinkAction] = useState(null);
  const [linkText, setLinkText] = useState('');
  const [status, setStatus] = useState('fetching');
  const [showSnackBar, setShowSnackBar] = useState(false);

  const [changes, setChanges] = useState();
  const [openChanges, setOpenChanges] = useState(false);
  const [loadingChangeLogs, setLoadingChangeLogs] = useState(false);
  const [openMarkAsInactiveModal, setOpenMarkAsInactiveModal] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [warnings, setWarnings] = useState([]);
  const [fetchingWarnings, setFetchingWarnings] = useState(true);
  const [news, setNews] = useState([]);
  const [newsTypes, setNewsTypes] = useState([]);
  const [riaInvestorParent, setRiaInvestorParent] = useState([]);
  const [fetchingNews, setFetchingNews] = useState(true);

  const [hasChanges, setHasChanges] = useState(false);
  const [isFromUserChange, setIsFromUserChange] = useState(false);

  const dupWarnings = warnings.filter((warning) => warning.type_of_warning === "potential_dup_warning" && warning.status === "pending");

  const [relatedInvestorsOptionMerge, setRelatedInvestorsOptionMerge] = useState([]);

  const adminNotes = useAdminNotes(team?.id, "PrivateWealthTeam", snackbar, PrivateWealthTeamsService.notes);
  const rejectionsNotes = useAdminNotes(team?.id, "PrivateWealthTeam", snackbar, PrivateWealthTeamsService.notes);

  const [avatarFile, setAvatarFile] = useState(null);
  const [fileUploadingSnackBar, setFileUploadingSnackBar] = useState({ showSnackBar: false });
  const [openMerger, setOpenMerger] = useState(false);
  const [openAddLocation, setOpenAddLocation] = useState(false);
  const [teamsToMergePreDraft, setTeamsToMergePreDraft] = useState([]);
  const [contactsPreDraft, setContactsPreDraft] = useState([]);
  const [fetchingTeamsToMergePreDraft, setFetchingTeamsToMergePreDraft] = useState(true);
  const [pwtType, setPwtType] = useState(null);
  const [loading, setLoading] = useState(false);

  const [mergeRelations, setMergeRelations] = useState([]);
  const [mergedIn, setMergedIn] = useState(null);
  const [fetchingMergeRelations, setFetchingMergeRelations] = useState(true);

  const { reset: resetEmail, validate: validateEmail, ...email } = useFieldWithValidation(
    "text",
    teamFirmInfo.email || "",
    (value) => ((validEmail(value) && value || _.isEmpty(value))),
    true,
    ({ target: { name, value } }) => handleOnChangeInField(name, value),
    (value) => (value && !validEmail(value)) ? "The email entered is not a valid email." : null
  );

  const { reset: resetUrl, validate: validateUrl, ...url } = useFieldWithValidation(
    "text",
    teamFirmInfo.url || "",
    (value) => (validUrl(value) || _.isEmpty(value)),
    true,
    ({ target: { name, value } }) => handleOnChangeInField(name, value),
    (value) => (value && !validUrl(value)) ? "The url entered is not a valid url." : null
  );

  const [optionsData, setOptionsData] = useState(optionDataInitialState);

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedCrds, setSelectedCrds] = useState([]);
  const [selectedFormerContacts, setSelectedFormerContacts] = useState([]);
  const [selectedFormerCrds, setSelectedFormerCrds] = useState([]);
  const [formers, setFormers] = useState(false);
  const [openMigrateModal, setOpenMigrateModal] = useState(false);
  const [locationSuggestion, setLocationSuggestion] = useState({});

  const [selectedMaTransactionId, setSelectedMaTransactionId] = useState(null);
  const [openMaTransactionModal, setOpenMaTransactionModal] = useState(false);

  const handleOpenMaTransaction = (maTransactionDbId) => {
    // maTransactionDbId is now the actual database ID (number)
    setSelectedMaTransactionId(maTransactionDbId);
    setOpenMaTransactionModal(true);
  };

  const handleCloseMaTransaction = () => {
    setOpenMaTransactionModal(false);
    setSelectedMaTransactionId(null);
  };

  const warningTeams = warnings.filter((warning) => (warning.type_of_warning === "potential_dup_warning" && warning.status === "pending")).map((warning) => (warning.extra_data.teams)).flat().filter((team) => !team.merged_at);

  const handleSelectOne = (_event, id, crdId) => {
    const selectedIndex = selectedContacts.indexOf(id);
    let newSelectedContacts = [];
    let newCrds = [];

    if (selectedIndex === -1) {
      newSelectedContacts = newSelectedContacts.concat(selectedContacts, id);
      newCrds = newCrds.concat(selectedCrds, crdId);
    } else if (selectedIndex === 0) {
      newSelectedContacts = newSelectedContacts.concat(selectedContacts.slice(1));
      newCrds = newCrds.concat(selectedCrds.slice(1));
    } else if (selectedIndex === selectedContacts.length - 1) {
      newSelectedContacts = newSelectedContacts.concat(selectedContacts.slice(0, -1));
      newCrds = newCrds.concat(selectedCrds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedContacts = newSelectedContacts.concat(
        selectedContacts.slice(0, selectedIndex),
        selectedContacts.slice(selectedIndex + 1)
      );
      newCrds = newCrds.concat(
        selectedCrds.slice(0, selectedIndex),
        selectedCrds.slice(selectedIndex + 1)
      );
    }

    setSelectedContacts(newSelectedContacts);
    setSelectedCrds(newCrds);
  };

  const handleSelectOneFormer = (_event, id, crdId) => {
    const selectedIndex = selectedFormerContacts.indexOf(id);
    let newSelectedContacts = [];
    let newCrds = [];

    if (selectedIndex === -1) {
      newSelectedContacts = newSelectedContacts.concat(selectedFormerContacts, id);
      newCrds = newCrds.concat(selectedFormerCrds, crdId);
    } else if (selectedIndex === 0) {
      newSelectedContacts = newSelectedContacts.concat(selectedFormerContacts.slice(1));
      newCrds = newCrds.concat(selectedFormerCrds.slice(1));
    } else if (selectedIndex === selectedFormerContacts.length - 1) {
      newSelectedContacts = newSelectedContacts.concat(selectedFormerContacts.slice(0, -1));
      newCrds = newCrds.concat(selectedFormerCrds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedContacts = newSelectedContacts.concat(
        selectedFormerContacts.slice(0, selectedIndex),
        selectedFormerContacts.slice(selectedIndex + 1)
      );
      newCrds = newCrds.concat(
        selectedFormerCrds.slice(0, selectedIndex),
        selectedFormerCrds.slice(selectedIndex + 1)
      );
    }

    setSelectedFormerContacts(newSelectedContacts);
    setSelectedFormerCrds(newCrds);
  };

  useEffect(() => {
    if (team !== undefined) {
      setIsFromUserChange(false);
      handleDataFetch();
      handleDataFormerFetch();
      handleDataPotentialFetch();
    }
  }, [team]);

  useEffectExceptOnMount(() => {
    if (hasChanges === false && initialTotalItemsFormer !== -1 && initialTotalItems !== -1 &&
      (initialTotalItems !== totalItems || initialTotalItemsFormer !== totalItemsFormer)) {
      setHasChanges(true);
    }
  }, [totalItems, totalItemsFormer]);

  useEffect(() => {
    handleFetchOptions({ team_id: team?.id });
  }, []);

  useEffectExceptOnMount(() => {
    setSelectedSpecialties(getSelectedOptions(teamFirmInfo?.specialties?.map(i => Number(i)), optionsData.services));
    setExcludedSelectedSpecialties(getSelectedOptions(teamFirmInfo?.excluded_specialties?.map(i => Number(i)), optionsData.services));
    setSelectedClientTypes(getSelectedOptions(teamFirmInfo?.client_types?.map(i => Number(i)), optionsData.clientTypes));
    setExcludedSelectedClientTypes(getSelectedOptions(teamFirmInfo?.excluded_client_types?.map(i => Number(i)), optionsData.clientTypes));
    setSelectedInvestmentUtilized(getSelectedOptions(teamFirmInfo?.investments_utilized?.map(i => Number(i)), optionsData.investmentUtilized));
    setExcludedSelectedInvestmentUtilized(getSelectedOptions(teamFirmInfo?.excluded_investments_utilized?.map(i => Number(i)), optionsData.investmentUtilized));
    setPwtType(optionsData.pwtTypes.find((pwtType) => pwtType.id === teamFirmInfo?.pwt_type));
  }, [optionsData, teamFirmInfo]);

  const fetchWarnings = async () => {
    setFetchingWarnings(true);
    const { data: { warnings: teamWarnings } } = await PrivateWealthTeamsService.warnings(team.id, session);
    setWarnings(teamWarnings);
    setFetchingWarnings(false);
  };

  const fetchNews = async () => {
    setFetchingNews(true);
    const { data: { news, news_types, ria_investor } } = await PrivateWealthTeamsService.news(team.id, session);
    setNews(news);
    setNewsTypes(news_types);
    setRiaInvestorParent(ria_investor);
    setFetchingNews(false);
  };

  const fetchMergeRelations = async () => {
    setFetchingMergeRelations(true);
    const { data: { incoming_merges, outgoing_merges } } = await PrivateWealthTeamsService.mergeRelations(team.id, session);
    setMergeRelations(incoming_merges);
    setMergedIn(outgoing_merges);
    setFetchingMergeRelations(false);
  };

  const fetchTeamsToMergePreDraft = async () => {
    setFetchingTeamsToMergePreDraft(true);
    const { data } = await PrivateWealthTeamsService.teamsToMergePreDraft(team.id, session);
    setTeamsToMergePreDraft(data.teams);
    setContactsPreDraft(data.contacts);
    setFetchingTeamsToMergePreDraft(false);
  };

  const handleFetchOptions = (params = {}) => {
    const newParams = {
      page: 1,
      per_page: 50,
      ...params
    };

    PrivateWealthTeamsService.extraOptions(session, newParams).then((response) => {
      const { data: { cities, countries, states, services, investments_utilized, client_types, pwt_types } } = response;
      setOptionsData({
        cities,
        countries,
        states,
        services,
        investmentUtilized: investments_utilized,
        clientTypes: client_types,
        pwtTypes: pwt_types,
        unitedStatesId: countries?.find((c) => c.name === 'United States')?.id,
      });
    });
  };

  const handleChangeRowsPerPage = (event) => {
    event.persist();
    setRowsPerPage(event.target.value);
    handleDataFetch({ per_page: event.target.value });
  };

  const handleChangeRowsPerPageFormer = (event) => {
    event.persist();
    setRowsPerPageFormer(event.target.value);
    handleDataFormerFetch({ per_page: event.target.value });
  };

  const handleChangeRowsPerPagePotential = (event) => {
    event.persist();
    setRowsPerPagePotential(event.target.value);
    handleDataPotentialFetch({ per_page: event.target.value });
  };

  const handleDataFetchPage = (event, page) => {
    event.persist();
    event.preventDefault();
    handleDataFetch({ page: page + 1 });
  };

  const handleDataFetchPageFormer = (event, page) => {
    event.persist();
    event.preventDefault();
    handleDataFormerFetch({ page: page + 1 });
  };

  const handleDataFetchPagePotential = (event, page) => {
    event.persist();
    event.preventDefault();
    handleDataPotentialFetch({ page: page + 1 });
  };

  const handleSort = (event, prop) => {
    const direction = sortBy === prop && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortBy(prop);
    setSortDirection(direction);
    handleDataFetch({ sort_by: prop, sort_direction: direction });
  };

  const handleSortFormer = (event, prop) => {
    const direction = formerSortBy === prop && formerSortDirection === 'asc' ? 'desc' : 'asc';
    setFormerSortBy(prop);
    setFormerSortDirection(direction);
    handleDataFormerFetch({ sort_by: prop, sort_direction: direction });
  };

  const handleSortPotential = (event, prop) => {
    const direction = potentialSortBy === prop && potentialSortDirection === 'asc' ? 'desc' : 'asc';
    setPotentialSortBy(prop);
    setPotentialSortDirection(direction);
    handleDataPotentialFetch({ sort_by: prop, sort_direction: direction });
  };

  const handleDataFetch = (params = {}) => {
    let newParams = {
      page: 1,
      per_page: 50,
      sort_by: sortBy,
      sort_direction: sortDirection,
      ...params
    };

    setStatus('fetching');

    PrivateWealthTeamsService.members(team.id, session, newParams).then((response) => {
      const { team_members, pagy, status: responseStatus } = response.data;
      setCurrentPage(pagy.page - 1);
      setTotalItems(pagy.count);
      if (initialTotalItems === -1) {
        setInitialTotalItems(pagy.count);
      }
      setTeamMembers(team_members);
      setStatus(responseStatus);
    });
  };

  const handleDataFormerFetch = (params = {}) => {
    let newParams = {
      page: 1,
      per_page: 50,
      sort_by: formerSortBy,
      sort_direction: formerSortDirection,
      ...params
    };

    setStatus('fetching');

    PrivateWealthTeamsService.formerTeamMembers(team.id, session, newParams).then((response) => {
      const { former_team_members, pagy_former, status: responseStatus } = response.data;
      setCurrentPageFormer(pagy_former.page - 1);
      setTotalItemsFormer(pagy_former.count);
      if (initialTotalItemsFormer === -1) {
        setInitialTotalItemsFormer(pagy_former.count);
      }
      setFormerTeamMembers(former_team_members);
      setStatus(responseStatus);
    });
  };

  const handleDataPotentialFetch = (params = {}) => {
    let newParams = {
      page: 1,
      per_page: 50,
      sort_by: potentialSortBy,
      sort_direction: potentialSortDirection,
      ...params
    };

    setStatus('fetching');

    PrivateWealthTeamsService.potentialTeamMembers(team.id, session, newParams).then((response) => {
      const { potential_team_members, pagy_potential, status: responseStatus } = response.data;
      setCurrentPagePotential(!!pagy_potential ? pagy_potential.page - 1 : 0);
      setTotalItemsPotential(!!pagy_potential ? pagy_potential.count : 0);
      setPotentialTeamMembers(potential_team_members);
      setStatus(responseStatus);
    });
  };

  const toggleExpandFormInfo = () => {
    setExpandFirmInfo(!expandFirmInfo);
  };

  const toggleExpandTeamMembers = () => {
    setExpandTeamMembers(!expandTeamMembers);
  };

  const toggleExpandPotentialTeamMembers = () => {
    setExpandPotentialTeamMembers(!expandPotentialTeamMembers);
  };

  const handleOpenContactModal = (contactId) => {
    setSelectedContactCrdId(contactId);
    setOpenContactModal(true);
  };

  const handleCloseContactModal = () => {
    setOpenContactModal(false);
    setSelectedContactCrdId(null);
  };

  const onOpenNotes = () => adminNotes.setOpen(true);

  const onOpenRejections = () => rejectionsNotes.setOpen(true);

  const handleReject = (event) => {
    reject(event);
    rejectionsNotes.setOpen(false);
  };

  const handleOnChangeInField = (field, value, byUser = true) => {
    if (byUser) setHasChanges(true);
    setTeamFirmInfo(prevState => ({
      ...prevState, [field]: value
    }));
  };

  useEffect(() => {
    if (team !== undefined) {
      setStatus('fetching');
      PrivateWealthTeamsService.edit(team.id, session).then((response) => {
        const { firm_info, status: responseStatus } = response.data;
        setTeamFirmInfo(firm_info);
        fetchWarnings();
        fetchAccolades();
        fetchNews();
        fetchMergeRelations();
        if (team.stage === 'pre_draft') {
          fetchTeamsToMergePreDraft();
        }
        fetchAumAggregations();
        setStatus('fetched');
      }).catch(function (thrown) {
        console.log('error', thrown.message);
      });
    }
  }, [team]);

  const handleCreatePrivateWealthTeam = (teamFirmInfo) => {
    setLoading(true);
    PrivateWealthTeamsService.create(session, { ...teamFirmInfo, suggestion: locationSuggestion }).then((response) => {
      const { data: { status, message, error_link, private_wealth_team } } = response;
      setLoading(false);
      if (status === 'success') {
        if (avatarFile) {
          setFileUploadingSnackBar({
            showSnackBar: true,
            snackBarMessage: 'The photo is uploading please wait',
            stayOpen: true,
          });
          PrivateWealthTeamsService.uploadAvatar(private_wealth_team.id, session, avatarFile).then(({ data }) => {
            if (data?.photo?.url) {
              afterCreate();
            } else {
              setFileUploadingSnackBar({
                showSnackBar: true,
                snackBarMessage: 'This photo is not supported please try another one',
                stayOpen: true,
              });
              setTimeout(() => {
                afterCreate();
              }, 3500);
            }
          });
        } else {
          afterCreate();
        }
      } else {
        setErrorMessage(message);
        setLinkAction(`admin/live_data/private_wealth_teams?team_id=${error_link}`);
        if (error_link) {
          setLinkText("Go To Group");
        } else {
          setLinkText(null);
        }
        setShowSnackBar(true);
        console.log('message', message);
      }
    });
  };

  const handleCloseFileSnackbar = () => {
    setFileUploadingSnackBar({ showSnackBar: false });
  };

  const handleUpdatePrivateWealthTeam = (teamFirmInfo) => {
    setLoading(true);
    updateWarnings().then(() => {
      PrivateWealthTeamsService.update(team.id, session, { ...teamFirmInfo, suggestion: locationSuggestion }).then(async (response) => {
        const {
          status: statusUpdate,
          message,
          team: updatedTeam,
          error_link,
        } = response.data;
        setLoading(false);
        if (statusUpdate === 'success') {
          // Refresh team data from server to get updated historical blueprints with MA transaction data
          try {
            const response = await PrivateWealthTeamsService.edit(updatedTeam.id, session);
            const freshTeamData = response.data.firm_info;

            // Update historical blueprints in teamFirmInfo with fresh data from server
            if (freshTeamData && freshTeamData.historical_blueprints) {
              setTeamFirmInfo(prevState => ({
                ...prevState,
                historical_blueprints: freshTeamData.historical_blueprints.map(bp => ({...bp}))
              }));
            }
          } catch (error) {
            console.error('Error refreshing team data:', error);
          }

          onDataUpdate(updatedTeam, itemIndex);
        } else {
          setErrorMessage(message);
          setLinkAction(`/admin/live_data/private_wealth_teams?team_id=${error_link}`);
          if (error_link) {
            setLinkText("Go To Group");
          } else {
            setLinkText(null);
          }
          setShowSnackBar(true);
          console.log('message', message);
        }
      });
    });
  };

  const handleFetchContacts = async (query) => {
    const response = await PrivateWealthTeamsService.contactsByInvestor(session, { query: query, crd_id: team.crd_id, team_id: team.id });
    return response;
  };

  const handleSpecialtiesChange = (specialty) => {
    const removedSpecialties = selectedSpecialties.filter(x => !specialty.includes(x));
    const excludedSpecialties = [...selectedExcludedSpecialties, ...removedSpecialties];
    const finalExcludedSpecialties = excludedSpecialties.filter(ar => !specialty.find(rm => (rm.id === ar.id)));

    setExcludedSelectedSpecialties(finalExcludedSpecialties);
    setSelectedSpecialties(specialty);
    handleOnChangeAutocomplete('specialties', specialty);
    handleOnChangeAutocomplete('excluded_specialties', finalExcludedSpecialties);
  };

  const handleInvestmentUtilizedChange = (investmentUtilized) => {
    const removedInvestmentUtilized = selectedInvestmentUtilized.filter(x => !investmentUtilized.includes(x));
    const excludedInvestmentUtilized = [...selectedExcludedInvestmentUtilized, ...removedInvestmentUtilized];
    const finalExcludedInvestmentUtilized = excludedInvestmentUtilized.filter(ar => !investmentUtilized.find(rm => (rm.id === ar.id)));

    setExcludedSelectedInvestmentUtilized(finalExcludedInvestmentUtilized);
    setSelectedInvestmentUtilized(investmentUtilized);
    handleOnChangeAutocomplete('investments_utilized', investmentUtilized);
    handleOnChangeAutocomplete('excluded_investments_utilized', finalExcludedInvestmentUtilized);
  };

  const handleClientTypesChange = (clientType) => {
    const removedClientTypes = selectedClientTypes.filter(x => !clientType.includes(x));
    const excludedClientTypes = [...selectedExcludedClientTypes, ...removedClientTypes];
    const finalExcludedClientTypes = excludedClientTypes.filter(ar => !clientType.find(rm => (rm.id === ar.id)));

    setExcludedSelectedClientTypes(finalExcludedClientTypes);
    setSelectedClientTypes(clientType);
    handleOnChangeAutocomplete('client_types', clientType);
    handleOnChangeAutocomplete('excluded_client_types', finalExcludedClientTypes);
  };

  const investors = (query) => {
    return GlobalSearchService.ria_investors(query, session);
  };

  const handleOnChange = (event) => {
    event.persist();

    if (['aum', 'minimum_account_size'].includes(event.target.name) && event.target.value) {
      event.target.value = event.target.value.replace(/[^0-9.-]+/g, "");
    }

    setHasChanges(true);

    setTeamFirmInfo(prevState => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSubmit = (event, newStage) => {
    event.persist();
    event.preventDefault();

    const blankValues = Object.keys(_.pickBy(teamFirmInfo, (item) => ([null, undefined, ''].includes(item) || (item.constructor.name === 'Array' && _.isEmpty(item)))));

    if (!_.isEmpty(_.intersection(blankValues, ['team_name', 'crd_id']))) {
      let message = {};
      blankValues.forEach((value) => {
        message[value] = "Can't be blank!";
      });
      setErrorMessage(message);
    } else if (team !== undefined) {
      const payload = _.omit(teamFirmInfo, ['aum', 'minimum_account_size']);
      if (newStage) {
        const visibleOnPlatform = newStage === 'published';
        handleUpdatePrivateWealthTeam({
          ...payload,
          stage: newStage,
          visible_on_platform: visibleOnPlatform,
        });
      } else {
        handleUpdatePrivateWealthTeam(payload);
      }
    } else {
      const payload = _.omit(teamFirmInfo, ['aum', 'minimum_account_size']);
      handleCreatePrivateWealthTeam(payload);
    }
  };

  const markAsReviewed = (event) => {
    event.persist();
    event.preventDefault();
    setLoading(true);
    PrivateWealthTeamsService.markAsReviewed(team.id, session).then((response) => {
      const { status: statusUpdate, message } = response.data;
      setLoading(false);
      if (statusUpdate === 'success') {
        updateWarnings().then(() => onDataUpdate());
      } else {
        setErrorMessage(message);
        setShowSnackBar(true);
        console.log('message', message);
      }
    });
  };

  const handleUnmerge = () => {
    openConfirmDialog({
      title: 'Unmerge Team',
      message: 'Are you sure you want to unmerge this team? It will become a live, independent team again.',
      onConfirm: () => {
        setLoading(true);
        PrivateWealthTeamsService.unmerge(team.id, session).then((response) => {
          const { status: statusUpdate, message } = response.data;
          setLoading(false);
          if (statusUpdate === 'success') {
            onDataUpdate();
          } else {
            setErrorMessage(message);
            setShowSnackBar(true);
          }
        }).catch(function (thrown) {
          setLoading(false);
          setErrorMessage(thrown.response?.data?.message || 'Something went wrong. Please try again.');
          setShowSnackBar(true);
          console.log('Request canceled', thrown.message);
        });
      },
      confirmText: 'Unmerge',
      confirmColor: 'secondary'
    });
  };

  const markAsComplete = (event) => {
    handleSubmit(event, 'draft_complete');
  };

  const publish = (event) => {
    handleSubmit(event, 'published');
  };

  const reject = (event) => {
    handleSubmit(event, 'draft_rejected');
  };

  const createNewGroup = (event) => {
    handleSubmit(event, 'draft_incomplete');
  };

  const markAsBranchLocation = (event) => {
    handleSubmit(event, 'branch_location');
  };

  const ignorePreDraft = (event) => {
    handleSubmit(event, 'ignore_pre_draft');
  };

  const handleOnChangeAutocomplete = (field, selectedValue) => {
    let fieldData = {};
    switch (field) {
      case 'specialties':
        fieldData = { [field]: selectedValue.map(({ id }) => id) };
        break;
      case 'client_types':
        fieldData = { [field]: selectedValue.map(({ id }) => id) };
        break;
      case 'investments_utilized':
        fieldData = { [field]: selectedValue.map(({ id }) => id) };
        break;
      case 'excluded_investments_utilized':
        fieldData = { [field]: selectedValue.map(({ id }) => id) };
        break;
      case 'excluded_client_types':
        fieldData = { [field]: selectedValue.map(({ id }) => id) };
        break;
      case 'excluded_specialties':
        fieldData = { [field]: selectedValue.map(({ id }) => id) };
        break;
      default:
        fieldData = { [field]: selectedValue };
        break;
    }

    setTeamFirmInfo(prevState => ({
      ...prevState,
      ...fieldData,
    }));
    setHasChanges(true);
  };


  const handleContactChange = (riaContact) => {
    setSelectedSelectedContact(riaContact);
    setOpenModal(true)
  }

  const handleConfirmPreview = () => {
    handleAddContactToTeam(selectedContact)
    setOpenModal(false)
  }

  const handleInvestorChange = (riaInvestor) => {
    handleOnChangeAutocomplete('crd_id', riaInvestor ? riaInvestor.id : undefined);
    handleOnChangeAutocomplete('firm_name', riaInvestor ? riaInvestor.name : '');
  }

  const handlePwtTypeChange = (pwtType) => {
    setPwtType(pwtType);
    handleOnChangeAutocomplete('pwt_type', pwtType.id)
  }

  const handleAddContactToTeam = (riaContact) => {
    PrivateWealthTeamsService.addMember(team.id, session, { contact: riaContact, teamFirmInfoId: teamFirmInfo.id }).then((response) => {
      const {
        data: {
          status,
          message,
        }
      } = response;

      if (status === 'success') {
        afterAddContactToTeam();
      } else {
        setErrorMessage(message);
        setShowSnackBar(true)
        console.log('message', message)
      }
    })
  }

  const handleRemoveTeamMember = (teamMemberId) => {
    openConfirmDialog({
      title: 'Remove Team Member',
      message: 'Are you sure this contact no longer works for the wealth team?',
      onConfirm: () => {
        PrivateWealthTeamsService.removeMember(teamFirmInfo.id, session, { private_wealth_team_id: teamFirmInfo.id, team_member_id: teamMemberId }).then((response) => {
          const { status, message } = response.data
          if (status === 'success') {
            handleDataFetch();
            handleDataFormerFetch();
          }
        }).catch(function (thrown) {
          console.log('Request canceled', thrown.message);
        });
      },
      confirmText: 'Remove',
      confirmColor: 'secondary'
    });
  }

  const handleRemoveTeamMembers = () => {
    openConfirmDialog({
      title: 'Remove Team Members',
      message: 'Are you sure these contacts no longer work for the wealth team?',
      onConfirm: () => {
        PrivateWealthTeamsService.removeMembers(session, { members: selectedContacts }).then((response) => {
          setSelectedCrds([])
          setSelectedContacts([])
          handleDataFetch()
          handleDataFormerFetch()
        });
      },
      confirmText: 'Remove',
      confirmColor: 'secondary'
    });
  }

  const handleRemoveFormerTeamMembers = () => {
    openConfirmDialog({
      title: 'Remove Former Team Members',
      message: 'Are you sure you want to remove entirely this contacts, only select this if contact was added to the team in error',
      onConfirm: () => {
        PrivateWealthTeamsService.removeFormerMembers(session, { former_members: selectedFormerContacts }).then((response) => {
          setSelectedFormerContacts([])
          setSelectedFormerCrds([])
          handleDataFormerFetch();
          handleDataPotentialFetch();
        });
      },
      confirmText: 'Remove',
      confirmColor: 'secondary'
    });
  }

  const handleReviveTeamMember = (teamMemberId) => {
    openConfirmDialog({
      title: 'Revive Team Member',
      message: 'Are you sure this contact works for the wealth team?',
      onConfirm: () => {
        PrivateWealthTeamsService.reviveFormerMember(session, { private_wealth_team_id: teamFirmInfo.id, team_member_id: teamMemberId }).then((response) => {
          const { status, message } = response.data
          if (status === 'success') {
            handleDataFetch();
            handleDataFormerFetch();
          }
        }).catch(function (thrown) {
          console.log('Request canceled', thrown.message);
        });
      },
      confirmText: 'Revive',
      confirmColor: 'secondary'
    });
  }

  const handleAddToTeamMember = (contactCrdId) => {
    openConfirmDialog({
      title: 'Add to Team Member',
      message: 'Do you want to add this contact to Wealth Team?',
      onConfirm: () => {
        handleAddContactToTeam({ id: contactCrdId, name: '' });
      },
      confirmText: 'Add',
      confirmColor: 'secondary'
    });
  }

  const handleRemoveFormerTeamMember = (formerTeamMemberId) => {
    openConfirmDialog({
      title: 'Remove Former Team Member',
      message: 'Are you sure you want to remove entirely this contact, only select this if contact was added to the team in error',
      onConfirm: () => {
        PrivateWealthTeamsService.removeFormerMember(teamFirmInfo.id, session, { private_wealth_team_id: teamFirmInfo.id, former_team_member_id: formerTeamMemberId }).then((response) => {
          const { status, message } = response.data

          if (status === 'success') {
            handleDataFormerFetch();
            handleDataPotentialFetch();
          }
        }).catch(function (thrown) {
          console.log('Request canceled', thrown.message);
        });
      },
      confirmText: 'Remove',
      confirmColor: 'secondary'
    });
  }

  const handleRemoveLocation = (location) => {
    openConfirmDialog({
      title: 'Remove Location',
      message: `You are removing a location, this can't be undone, Are you sure?`,
      onConfirm: () => {
        PrivateWealthTeamsService.removeLocation(team.id, { locationId: location.id }, session).then((response) => {

          const locations = teamFirmInfo.locations.filter(loc => loc.id !== location.id)
          setTeamFirmInfo({ ...teamFirmInfo, locations })
        });
      },
      confirmText: 'Remove',
      confirmColor: 'secondary'
    });
  }

  const afterAddContactToTeam = () => {
    handleDataFetch();
    handleDataPotentialFetch();
  }

  const openChangeLogs = () => {
    if (loadingChangeLogs) return; // Prevent multiple clicks

    setLoadingChangeLogs(true);
    PrivateWealthTeamsService.changeLog(team.id, session).then((response) => {
      const { changes } = response.data;
      setOpenChanges(true)
      setChanges(changes);
    }).catch((error) => {
      console.error('Error fetching change logs:', error);
    }).finally(() => {
      setLoadingChangeLogs(false);
    });
  }

  const handleCloseInactiveModal = () => {
    setOpenMarkAsInactiveModal(false)
    fetchPrivateWealthTeams()
    onClose()
  }

  const canUpdateTeamParent = (session.isSuperAdmin || session.inHouseResearch || session.qaResearcher)

  const handleSelectAll = (event) => {
    const ids = event.target.checked ? teamMembers.map(member => member.id) : []
    setSelectedContacts(ids)
    const crds = event.target.checked ? teamMembers.map(member => member.ria_contact_crd_id) : []
    setSelectedCrds(crds)
  }

  const handleSelectAllFormers = (event) => {
    const ids = event.target.checked ? formerTeamMembers.map(member => member.id) : []
    setSelectedFormerContacts(ids)
    const crds = event.target.checked ? formerTeamMembers.map(member => member.ria_contact_crd_id) : []
    setSelectedFormerCrds(crds)
  }

  const handleOpenMigrateModal = (formers) => {
    axios.get(
      `/v1/admin/ria_investors/investors_by_contact_crd_id`,
      {
        params: { crd_ids: formers ? selectedFormerCrds : selectedCrds },
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-User-Email': session.email,
          'X-Admin-User-Token': session.token
        }
      }
    ).then((response) => {
      const { status, message, data } = response.data

      if (status === 'fetched') {
        setFormers(formers);
        setRelatedInvestorsOptionMerge(data);
        setOpenMigrateModal(true);
      } else {
        setErrorMessage(message);
        setShowSnackBar(true)
        console.log('message', message)
      }
    }).catch(function (thrown) {
      console.log('Request canceled', thrown.message);
    });

  }

  const handleMigrate = (selectedGroupId) => {
    axios.put(
      `/v1/admin/private_wealth_teams/${team.id}/migrate_contacts`,
      { new_team_id: selectedGroupId, contacts: formers ? selectedFormerCrds : selectedCrds },
      {
        headers: {
          'X-Admin-User-Email': session.email,
          'X-Admin-User-Token': session.token
        }
      }
    ).then((response) => {
      setOpenMigrateModal(false)
      setSelectedCrds([])
      setSelectedContacts([])
      setSelectedFormerCrds([])
      setSelectedFormerContacts([])
      setFormers(false)
      handleDataFetch()
      handleDataFormerFetch()
    });
  }

  const handleChangeFileInput = (files) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = function (event) {
      setTeamFirmInfo(prevState => ({
        ...prevState,
        photo: file,
        photoPreview: event.target.result
      }));
    };
    reader.readAsDataURL(file);
    setAvatarFile(file);
    if (!!team) {
      setFileUploadingSnackBar({
        showSnackBar: true,
        snackBarMessage: 'The photo is uploading please wait',
        stayOpen: true,
      })
      PrivateWealthTeamsService.uploadAvatar(team.id, session, file).then(({ data }) => {
        if (data?.photo?.url) {
          setTimeout((event) => {
            setFileUploadingSnackBar({ showSnackBar: false })
          }, 2000)
        } else {
          setFileUploadingSnackBar({
            showSnackBar: true,
            snackBarMessage: 'This file is not supported please try another one',
            stayOpen: true,
          })
        }
      })
    }
  };

  const canChangeStage = () => {
    const errors = [];
    if (teamMembers.length === 0) {
      errors.push('At least one team member is required')
    }
    if (_.isEmpty(url.value) || url.error) {
      errors.push('Valid URL is required')
    }

    const locations = teamFirmInfo.locations.filter(location => !location._destroy);


    if (locations.length === 0) {
      errors.push('At least one location is required')
    } else {
      if (locations.some(hasLocationError)) {
        errors.push(VALIDATE_LOCATION_ERROR)
      }
      if (hasLocationDups(locations)) {
        errors.push(DUPLICATE_LOCATION_ERROR)
      }
      const hasPrincipalLocation = locations.some(location => location.is_principal);
      if (!hasPrincipalLocation) {
        errors.push(MISSING_PRINCIPAL_ERROR)
      }
    }

    const historical_blueprints = teamFirmInfo.historical_blueprints.filter(hb => !hb._destroy);
    if (historical_blueprints.some(hasHistoricalBlueprintError)) {
      errors.push(VALIDATE_HISTORICAL_BLUEPRINT_ERROR)
    }
    if (hasHistoricalBlueprintDups(historical_blueprints)) {
      errors.push(DUPLICATE_HISTORICAL_BLUEPRINT_ERROR)
    }

    if (team?.stage === 'draft_complete') {
      if (fetchingWarnings) {
        errors.push('Please wait until all warnings are fetched')
      } else {
        const pendingWarnings = warnings.filter(w => w.status === "pending")
        if (!_.isEmpty(pendingWarnings)) {
          errors.push('Please resolve all warnings before publishing')
        }
      }
    }

    if (team?.stage === 'pre_draft') {
      if (fetchingTeamsToMergePreDraft) {
        errors.push('Please wait until all warnings are fetched')
      } else {
        if (teamsToMergePreDraft.length === 0) {
          errors.push(VALIDATE_PRE_DRAFT_LOCATION_ERROR)
        }
      }
    }

    return errors;
  }

  const hasLocationDups = (locations) => {
    return _.uniqWith(locations, (l1, l2) =>
      (l1.address === l2.address || (_.isEmpty(l1.address) && _.isEmpty(l2.address))) &&
      (l1.address_2 === l2.address_2 || (_.isEmpty(l1.address_2) && _.isEmpty(l2.address_2))) &&
      l1.country_id === l2.country_id &&
      (l1.state === l2.state || (_.isEmpty(l1.state) && _.isEmpty(l2.state))) &&
      l1.city_id === l2.city_id &&
      (l1.zip_code === l2.zip_code || (_.isEmpty(l1.zip_code) && _.isEmpty(l2.zip_code)))
    ).length !== locations.length;
  }

  const hasHistoricalBlueprintDups = (hbp) => {
    return _.uniqWith(hbp, (l1, l2) =>
      (l1.previous_team_name === l2.previous_team_name || (_.isEmpty(l1.previous_team_name) && _.isEmpty(l2.previous_team_name))) &&
      (l1.current_team_name === l2.current_team_name) &&
      l1.current_parent_company_crd_id === l2.current_parent_company_crd_id &&
      l1.year === l2.year &&
      (l1.previous_parent_company_crd_id === l2.previous_parent_company_crd_id || (_.isEmpty(l1.previous_parent_company_crd_id) && _.isEmpty(l2.previous_parent_company_crd_id))) &&
      (l1.month === l2.month || (_.isEmpty(l1.month) && _.isEmpty(l2.month)))
    ).length !== hbp.length;
  }

  const hasLocationError = (location) => {
    if (location.ignore_validation === true) {
      return false;
    }

    return _.isEmpty(location.address) || location.country_id == null ||
      location.city_id == null || _.isEmpty(location.zip_code) ||
      (location.country_id === optionsData.unitedStatesId && _.isEmpty(location.state)) ||
      // branch_id-linked locations come from an RIA branch office location already validated
      // through that pipeline, so they don't go through Smarty validation here.
      (!location.branch_id && location.address_validated !== true);
  }

  const hasHistoricalBlueprintError = (historical) => {
    return !historical.year || !historical.previous_team_name || !historical.previous_parent_company_crd_id || !historical.current_parent_company_crd_id
  }

  const onRegenerateAiBio = () => {
    PrivateWealthTeamsService.regenerateAiBio(team.id, session).then((response) => {
      const { status, message } = response.data

      if (status === 'ok') {
        alert(message)
      } else {
        setErrorMessage(message);
        setShowSnackBar(true)
        console.log('message', message)
      }
    }).catch(function (thrown) {
      console.log('Request canceled', thrown.message);
    });
  }

  const canChangeStageAux = canChangeStage()

  const handleOnChangeNotes = (notesCount) => {
    if (onChangeNotes)
      onChangeNotes(team.id, notesCount)
  }

  const getSelectedOptions = (selected, options) => {
    return selected?.map((selectedId) => options.find(({ id }) => id === selectedId) || null).filter((option) => option !== null) || []
  }

  const openTeamMerger = (event) => {
    setOpenMerger(true);
  }

  const closeTeamMerger = (event) => {
    setOpenMerger(false);
  }

  const openAddLocationMerge = (event) => {
    setOpenAddLocation(true);
  }

  const closeAddLocationMerge = (event) => {
    setOpenAddLocation(false);
  }

  const updateWarnings = () => {
    return Promise.all(warnings.filter((warning) => warning.modified).map((warning) => {
      return PwtWarningsService.update(warning.id, session, { status: warning.status });
    }))
  }

  const mergeTeams = (team, teams) => {
    PrivateWealthTeamsService.mergeTeams(team.id, session, { teams: _.uniq(teams.map(({ id }) => id)) }).then((response) => {
      const {
        status: statusUpdate,
        message,
      } = response.data;

      if (statusUpdate === 'success') {
        snackbar.show("Groups have been merged successfully", "success");
        updateWarnings().then(() => {
          onDataUpdate()
        });
      } else {
        setErrorMessage(message);
        setShowSnackBar(true)
        console.log('message', message)
      }
    });
  }

  const handleManageLocation = (team, teams, action_to_apply) => {
    switch (action_to_apply) {
      case 'merge':
        var actionMessage = "Location have been merged successfull"
        break;
      case 'add':
        var actionMessage = "Location have been added successfull"
        break;
      case 'replace':
        var actionMessage = "Location have been replaced successfull"
        break;
      default:
        var actionMessage = null
    }

    PrivateWealthTeamsService.manageLocationTeams(team.id, session, { action_to_apply, teams: _.uniq(teams.map(({ id }) => id)) }).then((response) => {
      const {
        status: statusUpdate,
        message,
      } = response.data;

      if (statusUpdate === 'success') {
        alert(actionMessage);
        onDataUpdate()
      } else {
        setErrorMessage(message);
        setShowSnackBar(true)
      }
    });
  }

  const handleOnClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  }

  return {
    // Confirm Dialog hook states and handlers.
    confirmDialog,
    closeConfirmDialog,

    // Aum Aggregations hook states and handlers.
    aumAggregations,
    fetchingAumAggregations,
    handleChangeAumAggregation,
    handleBlurAumAggregation,
    handleAddAumAggregation,
    handleRemoveAumAggregation,

    // Accolades hook states and handlers.
    accolades,
    fetchingAccolades,
    fetchStatusAccolades,
    excludedAccolades,
    fetchingExcludedAccolades,
    fetchStatusExcludedAccolades,
    updatingAccolade,

    // Firm Info hook states and handlers.
    getFirmAumAggregationInfo,

    fetchAccolades,
    fetchExcludedAccolades,
    handleAddAccoladeToTeam,
    handleRemoveAccoladeFromTeam,

    // MA Transactions hook states and handlers.
    selectedMaTransactionId,
    openMaTransactionModal,

    handleOpenMaTransaction,
    handleCloseMaTransaction,

    // Estados básicos
    teamFirmInfo,
    setTeamFirmInfo,
    teamMembers,
    setTeamMembers,
    formerTeamMembers,
    setFormerTeamMembers,
    potentialTeamMembers,
    setPotentialTeamMembers,

    // Estados de selección de opciones
    selectedSpecialties,
    setSelectedSpecialties,
    selectedExcludedSpecialties,
    setExcludedSelectedSpecialties,
    selectedExcludedInvestmentUtilized,
    setExcludedSelectedInvestmentUtilized,
    selectedExcludedClientTypes,
    setExcludedSelectedClientTypes,
    selectedClientTypes,
    setSelectedClientTypes,
    selectedInvestmentUtilized,
    setSelectedInvestmentUtilized,

    // Estados de expansión
    expandFirmInfo,
    setExpandFirmInfo,
    expandTeamMembers,
    setExpandTeamMembers,
    expandPotentialTeamMembers,
    setExpandPotentialTeamMembers,

    // Estados de ordenamiento
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    formerSortBy,
    setFormerSortBy,
    potentialSortBy,
    setPotentialSortBy,
    formerSortDirection,
    setFormerSortDirection,
    potentialSortDirection,
    setPotentialSortDirection,

    // Estados de paginación
    totalItems,
    setTotalItems,
    totalItemsFormer,
    setTotalItemsFormer,
    totalItemsPotential,
    setTotalItemsPotential,
    initialTotalItems,
    setInitialTotalItems,
    initialTotalItemsFormer,
    setInitialTotalItemsFormer,
    currentPage,
    setCurrentPage,
    currentPageFormer,
    setCurrentPageFormer,
    currentPagePotential,
    setCurrentPagePotential,
    rowsPerPage,
    setRowsPerPage,
    rowsPerPageFormer,
    setRowsPerPageFormer,
    rowsPerPagePotential,
    setRowsPerPagePotential,

    // Estados de contactos
    selectedContact,
    setSelectedSelectedContact,
    selectedContactCrdId,
    setSelectedContactCrdId,
    openContactModal,
    setOpenContactModal,

    // Estados de errores y mensajes
    errorMessage,
    setErrorMessage,
    linkAction,
    setLinkAction,
    linkText,
    setLinkText,
    status,
    setStatus,
    showSnackBar,
    setShowSnackBar,

    // Estados de cambios y modales
    changes,
    setChanges,
    openChanges,
    setOpenChanges,
    openMarkAsInactiveModal,
    setOpenMarkAsInactiveModal,
    openPreview,
    setOpenPreview,
    openModal,
    setOpenModal,

    // Estados de warnings
    warnings,
    setWarnings,
    fetchingWarnings,
    setFetchingWarnings,
    news,
    setNews,
    newsTypes,
    setNewsTypes,
    riaInvestorParent,
    setRiaInvestorParent,
    fetchingNews,
    setFetchingNews,

    // Estados de cambios
    hasChanges,
    setHasChanges,
    isFromUserChange,
    setIsFromUserChange,

    // Estados de merge
    relatedInvestorsOptionMerge,
    setRelatedInvestorsOptionMerge,
    avatarFile,
    setAvatarFile,
    fileUploadingSnackBar,
    setFileUploadingSnackBar,
    openMerger,
    setOpenMerger,
    openAddLocation,
    setOpenAddLocation,
    teamsToMergePreDraft,
    setTeamsToMergePreDraft,
    contactsPreDraft,
    setContactsPreDraft,
    fetchingTeamsToMergePreDraft,
    setFetchingTeamsToMergePreDraft,
    pwtType,
    setPwtType,
    loading,
    setLoading,

    // Estados de merge relations
    mergeRelations,
    setMergeRelations,
    mergedIn,
    setMergedIn,
    fetchingMergeRelations,
    setFetchingMergeRelations,

    // Custom hooks
    adminNotes,
    rejectionsNotes,
    email,
    resetEmail,
    validateEmail,
    url,
    resetUrl,
    validateUrl,

    // Estados de opciones
    optionsData,
    setOptionsData,

    // Estados de selección de contactos
    selectedContacts,
    setSelectedContacts,
    selectedCrds,
    setSelectedCrds,
    selectedFormerContacts,
    setSelectedFormerContacts,
    selectedFormerCrds,
    setSelectedFormerCrds,
    formers,
    setFormers,
    openMigrateModal,
    setOpenMigrateModal,
    locationSuggestion,
    setLocationSuggestion,

    // Valores computados
    dupWarnings,
    warningTeams,
    canUpdateTeamParent,
    canChangeStageAux,

    // Handlers de selección
    handleSelectOne,
    handleSelectOneFormer,
    handleSelectAll,
    handleSelectAllFormers,

    // Funciones de fetch
    fetchWarnings,
    fetchNews,
    fetchMergeRelations,
    fetchTeamsToMergePreDraft,
    handleFetchOptions,
    handleFetchContacts,

    // Handlers de paginación
    handleChangeRowsPerPage,
    handleChangeRowsPerPageFormer,
    handleChangeRowsPerPagePotential,
    handleDataFetchPage,
    handleDataFetchPageFormer,
    handleDataFetchPagePotential,

    // Handlers de ordenamiento
    handleSort,
    handleSortFormer,
    handleSortPotential,

    // Handlers de data fetch
    handleDataFetch,
    handleDataFormerFetch,
    handleDataPotentialFetch,

    // Handlers de toggle
    toggleExpandFormInfo,
    toggleExpandTeamMembers,
    toggleExpandPotentialTeamMembers,

    // Handlers de modal
    handleOpenContactModal,
    handleCloseContactModal,

    // Handlers de notas
    onOpenNotes,
    onOpenRejections,
    handleReject,

    // Handlers CRUD
    handleOnChangeInField,
    handleCreatePrivateWealthTeam,
    handleCloseFileSnackbar,
    handleUpdatePrivateWealthTeam,

    // Handlers de autocomplete
    handleSpecialtiesChange,
    handleInvestmentUtilizedChange,
    handleClientTypesChange,
    handleOnChange,
    handleOnChangeAutocomplete,

    // Handlers de stage
    handleSubmit,
    markAsReviewed,
    handleUnmerge,
    markAsComplete,
    publish,
    reject,
    createNewGroup,
    markAsBranchLocation,
    ignorePreDraft,

    // Handlers de contactos
    handleContactChange,
    handleConfirmPreview,
    handleInvestorChange,
    handlePwtTypeChange,
    handleAddContactToTeam,
    afterAddContactToTeam,

    // Handlers de team members
    handleRemoveTeamMember,
    handleRemoveTeamMembers,
    handleRemoveFormerTeamMembers,
    handleReviveTeamMember,
    handleAddToTeamMember,
    handleRemoveFormerTeamMember,

    // Handlers de migración
    handleOpenMigrateModal,
    handleMigrate,

    // Handlers de archivos
    handleChangeFileInput,

    // Handlers de merge
    openTeamMerger,
    closeTeamMerger,
    openAddLocationMerge,
    closeAddLocationMerge,
    mergeTeams,
    handleManageLocation,
    updateWarnings,

    // Handler de cierre
    handleOnClose,

    // Otros handlers
    openChangeLogs,
    handleCloseInactiveModal,
    onRegenerateAiBio,
    handleOnChangeNotes,
    handleRemoveLocation,

    // Funciones auxiliares
    investors,
    getSelectedOptions,

    // Validaciones
    canChangeStage,
    hasLocationError,
    hasLocationDups,
    hasHistoricalBlueprintError,
    hasHistoricalBlueprintDups,

    loadingChangeLogs,
  };
};

export default usePrivateWealthTeamForm;
